import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import type { StatisticListFilters as FiltersType } from "./useStatisticList";
import { EMPTY_FILTERS } from "./useStatisticList";
import { parseDomainsInput } from "./ParseDomainsInput";

interface StatisticListFiltersProps {
  onChange: (filters: FiltersType) => void;
  regions: string[];
}

const LANGUAGE_OPTIONS = ["English", "Italian", "French", "German", "Dutch", "Polish"];

const DEBOUNCE_MS = 400;

export function StatisticListFilters(props: StatisticListFiltersProps) {
  const { onChange, regions } = props;
  const [local, setLocal] = useState<FiltersType>(EMPTY_FILTERS);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const updateField = <K extends keyof FiltersType>(
    field: K,
    value: FiltersType[K],
  ) => {
    setLocal((previous) => ({ ...previous, [field]: value }));
  };

  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      onChangeRef.current(local);
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, [local]);

  const handleLanguageChange = (value: string) => {
    const next: FiltersType = { ...local, language: value };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handleRegionChange = (value: string) => {
    const next: FiltersType = { ...local, region: value };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handleReset = () => {
    setLocal(EMPTY_FILTERS);
    onChangeRef.current(EMPTY_FILTERS);
  };

  const handleDomainsChange = (values: string[]) => {
    updateField("domains", parseDomainsInput(values.join("\n")));
  };

  const handleDomainsPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const parsed = parseDomainsInput(event.clipboardData.getData("text"));
    if (parsed.length === 0) {
      return;
    }
    event.preventDefault();
    updateField("domains", parseDomainsInput([...local.domains, ...parsed].join("\n")));
  };

  const hasActiveFilters =
    local.language !== "" || local.region !== "" || local.domains.length > 0;

  return (
    <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
      <Box flex="1 1 160px" maxWidth={250}>
        <TextField fullWidth
          size="small"
          label="Поиск по домену"
          value={local.search}
          onChange={(event) => updateField("search", event.target.value)}
        />
      </Box>
      <Box flex="1 1 130px" maxWidth={180}>
        <FormControl fullWidth size="small">
          <InputLabel>Язык</InputLabel>
          <Select
            value={local.language}
            label="Язык"
            onChange={(event) => handleLanguageChange(event.target.value)}
          >
            <MenuItem value="">Все</MenuItem>
            {LANGUAGE_OPTIONS.map((lang) => (
              <MenuItem key={lang} value={lang}>
                {lang}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box flex="1 1 130px" maxWidth={180}>
        <FormControl fullWidth size="small">
          <InputLabel>Регион</InputLabel>
          <Select
            value={local.region}
            label="Регион"
            onChange={(event) => handleRegionChange(event.target.value)}
          >
            <MenuItem value="">Все</MenuItem>
            {regions.map((reg) => (
              <MenuItem key={reg} value={reg}>
                {reg}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box flex="1 1 220px" maxWidth={340}>
        <Autocomplete
          multiple
          freeSolo
          fullWidth
          size="small"
          options={[]}
          value={local.domains}
          onChange={(_event, value) => handleDomainsChange(value)}
          renderInput={(parameters) => (
            <TextField {...parameters} label="Фильтр по доменам" onPaste={handleDomainsPaste} />
          )}
        />
      </Box>
      {hasActiveFilters && (
        <Button size="small" onClick={handleReset}>
          Сбросить фильтры
        </Button>
      )}
    </Box>
  );
}
